<template>
    <div class="policies">
        <div class="table">
            <div class="table__body">

                <Loading v-if="detailsLoading" />
                <template v-else>
                    <div v-if="!canViewNotes" class="table__no-data">
                        <NoData :type="'no-permission'" />
                    </div>
                    <div v-else-if="!computedNotes.length" class="table__no-data">
                        <NoData :type="'notes'" />
                    </div>
                    <template v-else>
                         
                        <div class="table__policies">
                            <div v-for="note in computedNotes" :key="note.id" class="note-div" :class="[
                                { 'animate__animated animate__zoomOutLeft': noteIsDeleting === note.id, },
                                { deleting: isNoteDeleted },
                            ]">
 
                                <div class="note-status">
                                    <div class="note-status-color">
                                        <div class="note-status-color-type" :class="{
                                            'note-priority-high': note.priority.id === 1,
                                            'note-priority-medium': note.priority.id === 2,
                                            'note-priority-low': note.priority.id === 3,
                                            'note-priority-verylow': note.priority.id === 4
                                        }"></div>
                                     

                                    </div>
                                    <div class="note-status-name">
                                        <p v-if="note.priority.id === 1">
                                          
                                     {{ $t("dashboard.notes.priority.high") }}
                                        </p>
                                        <p v-if="note.priority.id === 2">
                                            {{ $t("dashboard.notes.priority.medium") }}
                                        </p>
                                        <p v-if="note.priority.id === 3">
                                            {{ $t("dashboard.notes.priority.low") }}
                                        </p>

                                        <p v-if="note.priority.id === 4">
                                            {{ $t("dashboard.notes.priority.verylow") }}
                                        </p>
                                    </div>

                                </div>

                                <div class="note-owner">
                                    <div class="note-img-wrapper" v-if="note.agent.photo">
                                        <img v-if="note.agent.photo.preview" :src="note.agent.photo.preview">
                                    </div>
                                    <div class="note-img-wrapper no-img" v-else>
                                        <p>{{ parseInitials(note.agent?.first_name, note.agent?.last_name) }}</p>
                                    </div>

                                    <p class="note-owner-name">{{ note.agent?.first_name || '' }} {{ note.agent?.last_name || '' }}</p>
                                    <p class="note-owner-created">{{ formatDate(note.created_at) }}</p>
                                </div>

                                <div class=" note-actions-wrapper">
                                    <div class="icon-wrapper" @click="canEditNote ? markNote(note) : null"
                                        v-if="note.deadline && !note.is_done"
                                        :style="{ cursor: canEditNote ? 'pointer' : 'not-allowed' }">
                                        <img src="../../assets/icons/default/tick_icon_color.svg">
                                    </div>


                                    <div v-if="note.files.length" class="icon icon__pdf--active tool-icon"
                                        @click="canAccessNotes ? openNoteFilesModal(note) : null"
                                        :style="{ cursor: canAccessNotes ? 'pointer' : 'not-allowed' }">
                                    </div>
                                    <div v-else class="icon icon__pdf tool-icon" 
                                        @click="canAccessNotes ? openNoteFilesModal(note) : null"
                                        :style="{ cursor: canAccessNotes ? 'pointer' : 'not-allowed' }">
                                    </div>

                                    <div class="icon icon__edit" v-if="canEditNote"
                                        @click="showEditNoteModal(note)"></div>
                                    <div class="icon icon__edit" v-else style="cursor:no-drop;"></div>
                                    <div v-if="canDeleteNote" class="icon icon__delete tool-icon"
                                        :class="{ 'icon__delete--active': activeNoteId === note.id }" tabindex="-1"
                                        @focus="activeNoteId = note.id" @focusout="activeNoteId = null"
                                        :ref="'delete-' + note.id">
                                        <transition name="fade">
                                            <div class="delete" v-if="activeNoteId == note.id">
                                                <div class="delete__text">
                                                    {{$t(`addNoteModal.deleteNoteConfirmation`)}}
                                                </div>
                                                <div class="delete__buttons">
                                                    <div class="button button--confirm" @click="deleteNote(note.id)">
                                                    </div>
                                                    <div class="button button--cancel" @click="closeNoteDeletePopup">
                                                    </div>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                    <div v-else class="icon icon__delete tool-icon" style="cursor:no-drop;"></div>
                                </div>

                                <div class="note-title">
                                    <p>{{ note.title }}</p>
                                </div>
                                <div class="note-title-divider"></div>

                                <div class="note-description" v-if="note.description">
                                    <p style="white-space: pre-wrap; word-wrap: break-word;">{{ note.description }}</p>
                                </div>

                                <div class="note-deadline">
                                    <p>Deadline: </p>
                                    <p>{{ note.deadline }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import Loading from "@/partials/Loading";
import NoData from "@/partials/NoData";
import { parseInitials } from "@/helpers/entityParsers";
import { extractEntityTeamIds } from "@/helpers/teamAuthorization";

defineOptions({
    name: "NotesTable"
});

const store = useStore();

// State
const noteIsDeleting = ref("");
const activeNoteId = ref(null);
const isNoteDeleted = ref(false);
const detailsLoading = ref(false);
const lastFetchedCustomerId = ref(null);

// Computed
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const computedNotes = computed(() => store.getters.notes);
const costumerId = computed(() => store.getters.customerDetailsId);
const customerTeamIds = computed(() => extractEntityTeamIds(store.getters.customer ?? {}));
const can = (ability) => {
  if (isSuperAdmin.value) return true;
  return customerTeamIds.value.length > 0
    ? store.getters.canPerformActionOnEntity(ability, customerTeamIds.value)
    : false;
};
const canAccessNotes = computed(() => can("note_access"));
const canDeleteNote = computed(() => can("note_delete"));
const canEditNote = computed(() => can("note_edit"));
const canViewNotes = computed(() => can("note_view"));

// Methods
const fetchNotes = async () => {
    if (!costumerId.value) return;
    if (lastFetchedCustomerId.value === costumerId.value) return;

    detailsLoading.value = true;
    try {
        await store.dispatch("getNotes", costumerId.value);
        lastFetchedCustomerId.value = costumerId.value;
    } catch (error) {
        console.error(error);
    } finally {
        detailsLoading.value = false;
    }

};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const closeNoteDeletePopup = () => {
    // Note: If you have template refs, use useTemplateRef or individual refs in Vue 3
    activeNoteId.value = null;
};

const deleteNote = async (id) => {
    if (!canDeleteNote.value) return;
    noteIsDeleting.value = id;
    closeNoteDeletePopup();
    await store.dispatch("deleteNotes", {
        noteId: id,
        customerId: costumerId.value
    });
    isNoteDeleted.value = false;
};

const showEditNoteModal = (note) => {
    if (!canEditNote.value) return;
    store.commit("setShowAddNotesModal", true);
    store.commit("setIsNoteEditing", true);
    store.commit("setEditingNoteData", note);
};

const openNoteFilesModal = (note) => {
    if (!canAccessNotes.value) return;
    store.commit("setShowFilesEditModal", true);
    store.commit("setFilesToEditId", note.id);
    store.commit("setFilesToEdit", note.files);
    store.commit("setFilesEditMode", 'Notes');
};

const markNote = async (note) => {
    if (!canEditNote.value) return;
    if (note.deadline && note.customer) {
        detailsLoading.value = true;
        await store.dispatch("markNoteAsComplete", {
            noteId: note.id
        });
        await store.dispatch("getNotes", note.customer.id);
        detailsLoading.value = false;
    }
};

// Watchers
watch(costumerId, (newId, oldId) => {
    if (newId && newId !== oldId) {
        fetchNotes();
    }
});

// Lifecycle
onMounted(async () => {
    await fetchNotes();
});
</script>

<style lang="scss" scoped>
.delete {
    position: absolute;
    z-index: 3;
    background: var(--bg-1);
    right: calc(3% + 5px);
    top: 25%;
    transform: translateY(-50%);
    width: 300px;
    display: flex;
    align-items: center;
    border-radius: var(--radius-1);
    border: 1px solid var(--border-2);
    padding: 10px 20px;
    box-shadow: 0px 3px 6px var(--shadow-7);

    &__text {
        line-height: 20px;
        color: var(--text-9);
    }

    &__buttons {
        display: flex;
    }

    .button {
        width: 24px;
        height: 24px;
        cursor: pointer;
        transition: all 0.3s ease;

        &--confirm {
            background: url("../../assets/icons/default/confirm_icon.svg");
            margin: 0 15px;

            &:hover {
                background: url("../../assets/icons/default/confirm_hover_icon.svg");
            }
        }

        &--cancel {
            background: url("../../assets/icons/default/close_icon.svg");

            &:hover {
                background: url("../../assets/icons/default/close_hover_icon.svg");
            }
        }
    }
}

.note-div {
    width: 95%;
    box-shadow: 0px 0px 15px #0000001A;
    border-radius: 10px;
    padding: 15px;
    box-sizing: border-box;
    margin: 0 auto;
    margin-bottom: 50px;
    position: relative;
}

.note-div:first-child {
    margin-top: 50px;
}

.icon-wrapper {
    width: 24px;
    height: 24px;
}

.icon-wrapper img {
    width: 100%;
    height: 100%;
    cursor: pointer;
}

.note-actions-wrapper {
    display: flex;
    gap: 15px;
    align-items: center;
    justify-content: flex-end;
}

.note-title {
    width: 100%;
    margin-top: 10px;
}

.note-title p {
    color: #363636;
    font-size: 16px;
    font-weight: 600;
}

.note-title-divider {
    height: 1px;
    width: 160px;
    background-color: #EEEDED;
    margin: 10px 0;
}

.note-description {
    margin: 10px 0;
    max-width: 990px;
}

.note-description p {
    font-size: 14px;
    color: #363636;
    margin-bottom: 20px;
}

.note-deadline {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 20px;
    margin-right: 10px;
}

.note-deadline p {
    font-size: 16px;
    color: #7F8FA4;
}

.note-status {
    width: 140px;
    position: absolute;
    top: -20px;
    left: 0;
    background-color: #FFFFFF;
    display: flex;
    border-radius: 10px;
    align-items: center;
}

.note-status-name {
    flex-grow: 1;
    display: flex;
    border-radius: 10px;
    align-items: center;
    justify-content: center;
    height: 28px;
    margin-left: -25px;
    z-index: 1;
    background-color: #FFFFFF;
    box-shadow: 0px 8px 10px #00000026;
}


// [data-theme="dark"] .call-div {
//    p{ color: white;}
// }[

[data-theme="dark"] .note-status {
    background-color: transparent;
}

[data-theme="dark"] .note-status-name,
[data-theme="dark"] .note-status-color,
[data-theme="dark"] .no-img {
    background: var(--bg-7);

    p {
        color: white;
    }
}

[data-theme="dark"] .note-owner-name,
[data-theme="dark"] .note-owner-created {
    color: white;
}

[data-theme="dark"] .note-title,
[data-theme="dark"] .note-description,
[data-theme="dark"] .note-deadline {
    p {
        color: white;
    }
}

.note-status-name p {
    margin-left: 20px;
    color: #7F8FA4;
    font-size: 14px;
}

.note-status-color {
    z-index: 2;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #FFFFFF;
    box-shadow: 0px 8px 10px #00000026;
}

.note-status-color-type {
    width: 22px;
    height: 22px;
    border-radius: 50%;
}

.note-priority-high {
    background-color: #DB2222;
}

.note-priority-medium {
    background-color: #FF9E0F;
}

.note-priority-low  {
    background-color: #B9C2CD;
}
.note-priority-verylow {
    background-color: #c5cfd7;
}
.note-owner {
    position: absolute;
    top: 0%;
    left: 50%;
    text-align: center;
    transform: translate(-50%, -30%);
}

.note-img-wrapper {
    width: 46px;
    height: 46px;
    margin: 0 auto;
    border: 2px solid #FFFFFF;
    box-shadow: 0px 8px 10px #00000026;
    border-radius: 50%;
}

.no-img {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #FFFFFF;
    font-weight: 800;
}

.note-img-wrapper img {
    width: 100%;
    height: 100%;
}

.note-owner-name {
    margin: 10px 0;
    color: #363636;
    font-size: 14px;
    font-weight: bold;
}

.note-owner-created {
    color: #7F8FA4;
    font-size: 12px;
}

.loading { 
  height: 76vh;  
}
</style>
